import { useEffect, useState, type FormEvent } from 'react';
import { Color, SESSION_EXPIRED_COPY, SESSION_EXPIRED_EVENT, SESSION_RENEWED_EVENT, Size, t, type SignInOutcome } from '@bms2/shared';
import { Button, PasswordInput, TextInput } from '../components/controls';
import { AlertBanner } from '../components/controls/AlertBanner';
import { apiFetch } from '../pages/records/api';
import { SIGN_IN_PATH } from '../pages/LoginPage';
import { writeSessionToken } from '../session';
import styles from './shell.module.css';

/**
 * Session expiry (piece 21): an interrupting banner over the current page,
 * never a silent redirect. 🔨 It re-authenticates in place: the page keeps
 * its unsaved edits in the client while the sign-in runs, and every read
 * asks again once a session is issued. Approved copy: "Your session has
 * expired. Your unsaved changes are kept — sign in to continue."
 */
export function SessionExpiredBanner({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const [expired, setExpired] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    const onExpired = () => setExpired(true);
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, []);

  if (!expired) return null;

  const signIn = async (event: FormEvent) => {
    event.preventDefault();
    if (!email.trim() || !password) return;
    setBusy(true);
    const response = await apiFetch(`${apiBaseUrl}${SIGN_IN_PATH}`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email: email.trim(), password, code: code.trim() || null }) }).catch(/* silent: the session has expired, and a page without a session posts no report (piece 9). */ () => null);
    const outcome = response ? ((await response.json().catch(/* silent: the session has expired, and a page without a session posts no report (piece 9). */ () => null)) as SignInOutcome | null) : null;
    setBusy(false);
    if (!outcome) return setProblem(t('login.unreachable'));
    if (outcome.kind === 'refused') return setProblem(outcome.reason);
    if (outcome.kind === 'locked') return setProblem(outcome.reason);
    if (outcome.kind === 'enrol') return setProblem(t('session.enrolFirst'));
    // The password held and the code is next (decision 0325): the banner asks for it in its own field rather than opening a second step.
    if (outcome.kind === 'challenge') return setProblem(t('login.factorTitle'));
    writeSessionToken(outcome.token);
    setExpired(false);
    setPassword('');
    setCode('');
    setProblem(null);
    window.dispatchEvent(new CustomEvent(SESSION_RENEWED_EVENT));
  };

  return (
    <div className={styles.sessionExpired} data-session-expired="true">
      <AlertBanner tone="warning" message={problem ?? SESSION_EXPIRED_COPY} action={{ label: t('session.signInLabel'), onAction: () => document.querySelector<HTMLInputElement>('[data-session-field="email"] input')?.focus() }} />
      <form className={styles.reauth} onSubmit={(event) => void signIn(event)} data-session-form="true">
        <div data-session-field="email"><TextInput size={Size.Medium} label={t('login.email')} value={email} onChange={setEmail} type="email" autoComplete="username" required /></div>
        <PasswordInput size={Size.Medium} label={t('login.password')} value={password} onChange={setPassword} purpose="login" required />
        <TextInput size={Size.Medium} label={t('login.code')} value={code} onChange={setCode} type="text" autoComplete="one-time-code" />
        <Button size={Size.Medium} color={Color.Navy} text={[t('login.signIn')]} actions={[() => undefined]} type="submit" disabled={busy} />
      </form>
    </div>
  );
}
